import { estimateLinkfoxVideoCredits } from './linkfox-video-pricing.js';

export const DEFAULT_VIDEO_TYPE = 'SEED_FAST';
export const DEFAULT_VIDEO_MODE = 'single';

export const VIDEO_MODES = Object.freeze({
  single: {
    label: '图生视频',
    hint: '单张图片作为首帧生成视频。',
  },
  firstLast: {
    label: '首尾帧',
    hint: '上传首帧和尾帧，控制视频开始与结束画面；上传尾帧时按接口规则生成无声视频。',
  },
  multi: {
    label: '多图参考',
    hint: '上传多张参考图，使用 imageList 多图模式生成视频。',
  },
});

export const VIDEO_RULES = Object.freeze({
  KLING: {
    label: '可灵',
    times: [5, 10, 15],
    resolutions: ['720p', '1080p'],
    defaultResolution: '720p',
    supportsSingle: true,
    supportsList: true,
    maxImages: 7,
    ratios: ['', '1:1', '16:9', '9:16'],
    supportsLastFrame: true,
  },
  MODEL_OMNI: {
    label: '可灵 V3-Omni',
    times: [5, 10, 15],
    resolutions: ['720p', '1080p'],
    defaultResolution: '720p',
    supportsSingle: true,
    supportsList: true,
    maxImages: 7,
    ratios: ['', '1:1', '16:9', '9:16'],
    supportsLastFrame: true,
    voiceFixedOn: true,
  },
  WAN: {
    label: '万相',
    times: [5, 10, 15],
    resolutions: [],
    supportsSingle: true,
    supportsList: false,
    ratios: [''],
    cameras: ['single', 'multi'],
    supportsVoice: true,
    supportsLastFrame: true,
  },
  SEED: {
    label: 'Seedance/豆包',
    times: [5, 10, 15],
    resolutions: ['480p', '720p', '1080p'],
    defaultResolution: '480p',
    supportsSingle: true,
    supportsList: true,
    maxImages: 9,
    ratios: ['16:9', '9:16'],
    supportsVoice: true,
    supportsLastFrame: true,
  },
  SEED_FAST: {
    label: 'Seedance 2.0 Fast',
    times: [5, 10, 15],
    resolutions: ['480p', '720p'],
    defaultResolution: '480p',
    supportsSingle: true,
    supportsList: true,
    maxImages: 9,
    ratios: ['16:9', '9:16'],
    supportsLastFrame: true,
  },
  SEED_MINI: {
    label: 'Seedance 2.0 Mini',
    times: [5, 10, 15],
    resolutions: ['480p', '720p'],
    defaultResolution: '480p',
    supportsSingle: true,
    supportsList: true,
    maxImages: 9,
    ratios: ['16:9', '9:16'],
    supportsLastFrame: true,
  },
  HAILUO: {
    label: '海螺2.3',
    times: [6, 10],
    resolutions: ['768p', '1080p'],
    defaultResolution: '768p',
    supportsSingle: true,
    supportsList: false,
    ratios: [],
    voiceFixedOn: true,
  },
  HAPPY_HORSE: {
    label: 'Happy Horse',
    times: [5, 10, 15],
    resolutions: ['720p', '1080p'],
    defaultResolution: '720p',
    supportsSingle: false,
    supportsList: true,
    requiresList: true,
    maxImages: 9,
    ratios: ['16:9', '9:16'],
    voiceFixedOn: true,
  },
});

export function getVideoRule(type = DEFAULT_VIDEO_TYPE) {
  return VIDEO_RULES[type] || VIDEO_RULES[DEFAULT_VIDEO_TYPE];
}

export function isModeSupported(type = DEFAULT_VIDEO_TYPE, mode = DEFAULT_VIDEO_MODE) {
  const rule = getVideoRule(type);
  if (mode === 'multi') return Boolean(rule.supportsList);
  if (mode === 'firstLast') return Boolean(rule.supportsSingle && rule.supportsLastFrame);
  return Boolean(rule.supportsSingle);
}

export function getAvailableModes(type = DEFAULT_VIDEO_TYPE) {
  return Object.keys(VIDEO_MODES).filter((mode) => isModeSupported(type, mode));
}

export function normalizeVideoMode(type = DEFAULT_VIDEO_TYPE, mode = DEFAULT_VIDEO_MODE) {
  const rule = getVideoRule(type);
  if (rule.requiresList) return 'multi';
  if (isModeSupported(type, mode)) return mode;
  if (isModeSupported(type, DEFAULT_VIDEO_MODE)) return DEFAULT_VIDEO_MODE;
  return getAvailableModes(type)[0] || DEFAULT_VIDEO_MODE;
}

export function getInputMode({ type = DEFAULT_VIDEO_TYPE, mode = DEFAULT_VIDEO_MODE } = {}) {
  const normalizedMode = normalizeVideoMode(type, mode);
  return normalizedMode === 'multi' ? 'list' : 'single';
}

export function getTimeValues({ type = DEFAULT_VIDEO_TYPE, mode = 'single' } = {}) {
  const rule = getVideoRule(type);
  const normalizedMode = normalizeVideoMode(type, mode);
  if (type === 'KLING' && normalizedMode !== 'multi') {
    return rule.times.filter((value) => value !== 15);
  }
  return rule.times;
}

export function getImageLimit(type = DEFAULT_VIDEO_TYPE, mode = DEFAULT_VIDEO_MODE) {
  const normalizedMode = normalizeVideoMode(type, mode);
  if (normalizedMode !== 'multi') return 1;
  return getVideoRule(type).maxImages || 1;
}

export function shouldUseImageList(type = DEFAULT_VIDEO_TYPE, mode = DEFAULT_VIDEO_MODE) {
  return normalizeVideoMode(type, mode) === 'multi';
}

export function estimateVideoCredits({
  type = DEFAULT_VIDEO_TYPE,
  time = 10,
  resolution = '',
  mode = DEFAULT_VIDEO_MODE,
  voice = false,
  isPro = false,
} = {}) {
  const normalizedType = getVideoRule(type) === VIDEO_RULES[DEFAULT_VIDEO_TYPE] && type !== DEFAULT_VIDEO_TYPE
    ? DEFAULT_VIDEO_TYPE
    : type;
  return estimateLinkfoxVideoCredits({
    videoType: normalizedType,
    videoTime: time,
    resolution,
    inputMode: normalizeVideoMode(normalizedType, mode),
    voice,
    isPro,
  });
}
